import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Cloud,
  CloudOff,
  Folder,
  FolderPlus,
  RefreshCw,
  Trash2,
  ChevronRight,
  ChevronLeft,
  Loader2,
  HardDrive,
  AlertTriangle,
  Check,
  X,
  FileText,
  Database,
} from "lucide-react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function OneDriveSettings() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  
  // Folder browser state
  const [showBrowser, setShowBrowser] = useState(false);
  const [browserItems, setBrowserItems] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserPath, setBrowserPath] = useState([{ id: null, name: "OneDrive" }]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  
  // Confirm dialogs
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [syncingFolderId, setSyncingFolderId] = useState(null);

  useEffect(() => {
    fetchStatus();
    
    // Check for OAuth callback result
    const onedriveResult = searchParams.get("onedrive");
    if (onedriveResult === "success") {
      toast.success("OneDrive connected successfully!");
      fetchStatus();
    } else if (onedriveResult === "error") {
      const message = searchParams.get("message") || "Connection failed";
      toast.error(`OneDrive connection failed: ${message}`);
    }
  }, [searchParams]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/onedrive/status`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error("Error fetching OneDrive status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API}/onedrive/auth-url`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to initiate connection");
        setConnecting(false);
      }
    } catch (error) {
      toast.error("Failed to connect to OneDrive");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const response = await fetch(`${API}/onedrive/disconnect`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("OneDrive disconnected");
        setShowDisconnectConfirm(false);
        fetchStatus();
      } else {
        toast.error("Failed to disconnect");
      }
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const openFolderBrowser = async () => {
    setShowBrowser(true);
    setBrowserPath([{ id: null, name: "OneDrive" }]);
    await browseFolders(null);
  };

  const browseFolders = async (folderId) => {
    setBrowserLoading(true);
    try {
      const url = folderId 
        ? `${API}/onedrive/browse?folder_id=${folderId}`
        : `${API}/onedrive/browse`;
      
      const response = await fetch(url, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrowserItems(data.items || []);
      } else {
        toast.error("Failed to browse folders");
      }
    } catch (error) {
      toast.error("Failed to browse folders");
    } finally {
      setBrowserLoading(false);
    }
  };

  const navigateToFolder = async (folder) => {
    setBrowserPath([...browserPath, { id: folder.id, name: folder.name }]);
    await browseFolders(folder.id);
  };

  const navigateBack = async (index) => {
    const newPath = browserPath.slice(0, index + 1);
    setBrowserPath(newPath);
    await browseFolders(newPath[newPath.length - 1].id);
  };

  const addFolder = async () => {
    if (!selectedFolder) return;
    
    try {
      const response = await fetch(`${API}/onedrive/folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          folder_id: selectedFolder.id,
          folder_name: selectedFolder.name,
          folder_path: selectedFolder.path || "/" + selectedFolder.name,
        }),
      });
      
      if (response.ok) {
        toast.success(`Folder "${selectedFolder.name}" added`);
        setShowBrowser(false);
        setSelectedFolder(null);
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add folder");
      }
    } catch (error) {
      toast.error("Failed to add folder");
    }
  };

  const removeFolder = async (folderId) => {
    try {
      const response = await fetch(`${API}/onedrive/folders/${folderId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Folder removed");
        fetchStatus();
      } else {
        toast.error("Failed to remove folder");
      }
    } catch (error) {
      toast.error("Failed to remove folder");
    }
  };

  const syncFolder = async (folderId) => {
    setSyncingFolderId(folderId);
    try {
      const response = await fetch(`${API}/onedrive/folders/${folderId}/sync`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Sync started");
        // Poll for updates
        setTimeout(() => fetchStatus(), 2000);
        setTimeout(() => fetchStatus(), 5000);
        setTimeout(() => {
          fetchStatus();
          setSyncingFolderId(null);
        }, 10000);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start sync");
        setSyncingFolderId(null);
      }
    } catch (error) {
      toast.error("Failed to start sync");
      setSyncingFolderId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="w-5 h-5 text-blue-500" />
                OneDrive Integration
              </CardTitle>
              <CardDescription>
                Sync folders from OneDrive to Rex's knowledge base
              </CardDescription>
            </div>
            {status?.connected ? (
              <Badge variant="default" className="bg-green-600">
                <Check className="w-3 h-3 mr-1" />
                Connected
              </Badge>
            ) : (
              <Badge variant="secondary">
                <CloudOff className="w-3 h-3 mr-1" />
                Not Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Connection Status */}
          {status?.connected ? (
            <>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Cloud className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{status.user_name}</p>
                    <p className="text-sm text-slate-500">{status.user_email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <CloudOff className="w-4 h-4 mr-2" />
                  Disconnect
                </Button>
              </div>

              {/* Storage Usage */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-slate-600">
                    <Database className="w-4 h-4" />
                    Storage Used
                  </span>
                  <span className="font-medium">
                    {status.storage_used_mb} MB / {status.storage_limit_mb} MB
                  </span>
                </div>
                <Progress value={status.storage_percent} className="h-2" />
                {status.storage_percent > 90 && (
                  <div className="flex items-center gap-2 text-amber-600 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    Storage almost full. Consider removing some folders.
                  </div>
                )}
              </div>

              {/* Synced Folders */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-800">Synced Folders</h4>
                  <Button size="sm" onClick={openFolderBrowser} className="gap-2">
                    <FolderPlus className="w-4 h-4" />
                    Add Folder
                  </Button>
                </div>

                {status.folders?.length > 0 ? (
                  <div className="space-y-2">
                    {status.folders.map((folder) => (
                      <div
                        key={folder.folder_id}
                        className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Folder className="w-5 h-5 text-amber-500" />
                          <div>
                            <p className="font-medium text-slate-800">{folder.folder_name}</p>
                            <p className="text-xs text-slate-500">
                              {folder.folder_path} • {folder.file_count || 0} files
                              {folder.last_synced && ` • Last sync: ${new Date(folder.last_synced).toLocaleDateString()}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              folder.status === "completed" ? "default" :
                              folder.status === "syncing" ? "secondary" :
                              folder.status === "error" ? "destructive" : "outline"
                            }
                            className="text-xs"
                          >
                            {folder.status}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => syncFolder(folder.folder_id)}
                            disabled={syncingFolderId === folder.folder_id || folder.status === "syncing"}
                            className="h-8 w-8 p-0"
                          >
                            <RefreshCw className={`w-4 h-4 ${(syncingFolderId === folder.folder_id || folder.status === "syncing") ? "animate-spin" : ""}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFolder(folder.folder_id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500 border border-dashed border-slate-200 rounded-lg">
                    <Folder className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>No folders synced yet</p>
                    <p className="text-sm">Add folders to start syncing content to Rex</p>
                  </div>
                )}
              </div>

              {/* File Types Info */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">Supported file types:</p>
                <p>PDF, Word (.docx), PowerPoint (.pptx), Excel (.xlsx), Text (.txt, .md, .csv)</p>
                <p className="mt-1 text-blue-600">Max 25MB per file • Text content only (no images)</p>
              </div>
            </>
          ) : (
            /* Not Connected State */
            <div className="text-center py-8">
              <Cloud className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">Connect OneDrive</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Connect your Microsoft OneDrive to sync documents, proposals, and other files to Rex's knowledge base.
              </p>
              {status?.configured ? (
                <Button onClick={handleConnect} disabled={connecting} className="gap-2">
                  {connecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Cloud className="w-4 h-4" />
                  )}
                  Connect OneDrive
                </Button>
              ) : (
                <div className="p-4 bg-amber-50 rounded-lg text-amber-800 max-w-md mx-auto">
                  <AlertTriangle className="w-5 h-5 mx-auto mb-2" />
                  <p className="font-medium">OneDrive not configured</p>
                  <p className="text-sm mt-1">
                    Please add ONEDRIVE_CLIENT_ID and ONEDRIVE_CLIENT_SECRET to your environment variables.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Folder Browser Dialog */}
      <Dialog open={showBrowser} onOpenChange={setShowBrowser}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select a Folder to Sync</DialogTitle>
            <DialogDescription>
              Browse your OneDrive and select a folder to sync to Rex's knowledge base.
            </DialogDescription>
          </DialogHeader>
          
          {/* Breadcrumb */}
          <div className="flex items-center gap-1 text-sm text-slate-600 overflow-x-auto pb-2">
            {browserPath.map((item, index) => (
              <div key={index} className="flex items-center">
                {index > 0 && <ChevronRight className="w-4 h-4 mx-1 text-slate-400" />}
                <button
                  onClick={() => navigateBack(index)}
                  className="hover:text-blue-600 hover:underline whitespace-nowrap"
                >
                  {item.name}
                </button>
              </div>
            ))}
          </div>

          {/* Folder List */}
          <div className="border rounded-lg max-h-80 overflow-y-auto">
            {browserLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : browserItems.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Folder className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>This folder is empty</p>
              </div>
            ) : (
              <div className="divide-y">
                {browserItems.filter(item => item.type === "folder").map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer ${
                      selectedFolder?.id === item.id ? "bg-blue-50 border-l-2 border-blue-500" : ""
                    }`}
                    onClick={() => setSelectedFolder(item)}
                    onDoubleClick={() => navigateToFolder(item)}
                  >
                    <div className="flex items-center gap-3">
                      <Folder className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="font-medium text-slate-800">{item.name}</p>
                        <p className="text-xs text-slate-500">{item.child_count} items</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToFolder(item);
                      }}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {browserItems.filter(item => item.type === "file").map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3 text-slate-400"
                  >
                    <FileText className="w-5 h-5" />
                    <span className="text-sm">{item.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBrowser(false)}>
              Cancel
            </Button>
            <Button onClick={addFolder} disabled={!selectedFolder}>
              <FolderPlus className="w-4 h-4 mr-2" />
              Add "{selectedFolder?.name || "Folder"}"
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation */}
      <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect OneDrive?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all synced content from Rex's knowledge base. You can reconnect later, but you'll need to re-sync your folders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnect} className="bg-red-600 hover:bg-red-700">
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
